// Central data source for the AI Eye Symptom Checker.
// Kept as a plain JS module so the wizard is easy to extend without touching UI code.
// All user-visible strings include a `hi` (Hindi/Devanagari) counterpart so the
// wizard can render fully in Hindi without any DOM-based translator.

import {
  Eye, EyeOff, Sparkles, Zap, Droplet, Hand, Sun, AlertTriangle, Bug,
  ScanEye, Baby, Move3d, ShieldAlert, Wind, Circle, Focus, HelpCircle,
} from "lucide-react";

export const AGE_BANDS = [
  { key: "under-5",  label: "Under 5",     hi: "5 वर्ष से कम" },
  { key: "5-18",     label: "5–18",        hi: "5–18 वर्ष" },
  { key: "18-40",    label: "18–40",       hi: "18–40 वर्ष" },
  { key: "40-60",    label: "40–60",       hi: "40–60 वर्ष" },
  { key: "above-60", label: "Above 60",    hi: "60 वर्ष से अधिक" },
];

export const GENDERS = [
  { key: "male",        label: "Male",              hi: "पुरुष" },
  { key: "female",      label: "Female",            hi: "महिला" },
  { key: "other",       label: "Other",             hi: "अन्य" },
  { key: "prefer-not",  label: "Prefer not to say", hi: "बताना नहीं चाहते" },
];

export const EYE_SIDES = [
  { key: "right", label: "Right eye", hi: "दाहिनी आंख" },
  { key: "left",  label: "Left eye",  hi: "बायीं आंख" },
  { key: "both",  label: "Both eyes", hi: "दोनों आंखें" },
];

// Every symptom is also an SEO page slug — see /symptoms/{slug}.
export const SYMPTOMS = [
  { slug: "blurred-vision",             label: "Blurred vision",             hi: "धुंधला दिखना (blurred vision)",       icon: EyeOff },
  { slug: "sudden-vision-loss",         label: "Sudden vision loss",         hi: "अचानक दिखना बंद (sudden vision loss)", icon: ShieldAlert, danger: true },
  { slug: "gradual-vision-loss",        label: "Gradual vision loss",        hi: "धीरे-धीरे कम दिखना",                  icon: Eye },
  { slug: "eye-pain",                   label: "Eye pain",                   hi: "आंख में दर्द",                         icon: AlertTriangle },
  { slug: "red-eye",                    label: "Red eye",                    hi: "आंख का लाल होना",                      icon: Circle },
  { slug: "watering",                   label: "Watering",                   hi: "आंख से पानी आना",                       icon: Droplet },
  { slug: "itching",                    label: "Itching",                    hi: "आंख में खुजली",                         icon: Hand },
  { slug: "flashes",                    label: "Flashes",                    hi: "बिजली जैसी चमक (flashes)",              icon: Zap },
  { slug: "floaters",                   label: "Floaters",                   hi: "आंख में तैरते धब्बे (floaters)",         icon: Sparkles },
  { slug: "black-curtain",              label: "Black curtain / shadow",     hi: "आंख पर काला परदा / छाया",                icon: ShieldAlert, danger: true },
  { slug: "double-vision",              label: "Double vision",              hi: "दो-दो दिखना (double vision)",           icon: Move3d },
  { slug: "swelling",                   label: "Swelling",                   hi: "आंख/पलक में सूजन",                       icon: Wind },
  { slug: "eye-injury",                 label: "Eye injury",                 hi: "आंख में चोट",                            icon: AlertTriangle, danger: true },
  { slug: "foreign-body",               label: "Foreign body",               hi: "आंख में कुछ गिरना (foreign body)",       icon: Bug },
  { slug: "chemical-injury",            label: "Chemical injury",            hi: "आंख में केमिकल गिरना (chemical injury)", icon: ShieldAlert, danger: true },
  { slug: "light-sensitivity",          label: "Light sensitivity",          hi: "रोशनी से चुभन (photophobia)",            icon: Sun },
  { slug: "headache-with-eye-pain",     label: "Headache with eye pain",     hi: "सिर दर्द के साथ आंख का दर्द",             icon: Focus },
  { slug: "white-reflex-in-child",      label: "White reflex in child",      hi: "बच्चे की आंख में सफेद चमक (white reflex)", icon: Baby, danger: true },
  { slug: "squint",                     label: "Squint",                     hi: "भेंगापन (squint)",                        icon: ScanEye },
  { slug: "child-not-seeing-properly",  label: "Child not seeing properly",  hi: "बच्चे को ठीक से न दिखना",                 icon: Baby },
  { slug: "others",                     label: "Other / not listed",         hi: "अन्य / सूची में नहीं",                     icon: HelpCircle },
];

// Follow-up questions per symptom. Kept small — 3-6 questions max, all Yes/No/short.
// The wizard adapts based on which symptom the user picked in step 4.
const YN = [
  { v: "yes", label: "Yes", hi: "हाँ" },
  { v: "no",  label: "No",  hi: "नहीं" },
];

export const FOLLOWUPS = {
  "blurred-vision": [
    { key: "onset",   q: "When did it start?",                    hi_q: "यह कब शुरू हुआ?",                 opts: [{v:"sudden",label:"Suddenly",hi:"अचानक"},{v:"gradual",label:"Gradually",hi:"धीरे-धीरे"}] },
    { key: "pain",    q: "Is there any pain?",                    hi_q: "क्या दर्द है?",                    opts: YN },
    { key: "flashes", q: "Any new flashes of light?",             hi_q: "क्या नई बिजली जैसी चमक दिख रही है?", opts: YN },
    { key: "floaters",q: "Any new floaters?",                     hi_q: "क्या नए तैरते धब्बे दिख रहे हैं?",   opts: YN },
    { key: "recent-surgery", q: "Recent eye surgery (last 6 weeks)?", hi_q: "क्या पिछले 6 हफ्तों में आंख का ऑपरेशन हुआ है?", opts: YN },
  ],
  "sudden-vision-loss": [
    { key: "duration", q: "How long ago did it start?", hi_q: "यह कितने समय पहले शुरू हुआ?", opts: [{v:"hours",label:"Within hours",hi:"कुछ घंटों में"},{v:"day",label:"Today",hi:"आज"},{v:"days",label:"A few days",hi:"कुछ दिन पहले"}] },
    { key: "pain",     q: "Is there pain?",             hi_q: "क्या दर्द है?",                opts: YN },
    { key: "trauma",   q: "Any recent injury?",         hi_q: "क्या कोई हाल की चोट लगी है?",   opts: YN },
  ],
  "gradual-vision-loss": [
    { key: "months",       q: "Over how many months?", hi_q: "कितने महीनों में?", opts: [{v:"lt3",label:"Less than 3",hi:"3 से कम"},{v:"3-12",label:"3–12",hi:"3–12"},{v:"gt12",label:"More than a year",hi:"एक साल से अधिक"}] },
    { key: "night-vision", q: "Worse in dim light?",   hi_q: "कम रोशनी में अधिक खराब?", opts: YN },
    { key: "diabetes",     q: "Are you diabetic?",     hi_q: "क्या आपको मधुमेह है?",     opts: YN },
  ],
  "eye-pain": [
    { key: "severity",         q: "How severe is the pain?",  hi_q: "दर्द कितना तेज़ है?", opts: [{v:"mild",label:"Mild",hi:"हल्का"},{v:"moderate",label:"Moderate",hi:"मध्यम"},{v:"severe",label:"Severe",hi:"तेज़"}] },
    { key: "vomiting",         q: "Any nausea or vomiting?",   hi_q: "क्या मतली या उल्टी है?",           opts: YN },
    { key: "blurred-vision",   q: "Is vision blurred?",         hi_q: "क्या दिखना धुंधला है?",             opts: YN },
    { key: "light-sensitivity",q: "Light sensitivity?",         hi_q: "क्या रोशनी से चुभन है?",           opts: YN },
    { key: "contact-lens",     q: "Do you wear contact lenses?",hi_q: "क्या आप कॉन्टैक्ट लेंस पहनते हैं?", opts: YN },
    { key: "chemical-exposure",q: "Any chemical exposure?",     hi_q: "क्या कोई केमिकल लगा है?",           opts: YN },
  ],
  "red-eye": [
    { key: "discharge",      q: "Any discharge?",   hi_q: "क्या कोई स्राव है?",       opts: [{v:"none",label:"None",hi:"नहीं"},{v:"watery",label:"Watery",hi:"पानी जैसा"},{v:"sticky",label:"Sticky / yellow",hi:"चिपचिपा / पीला"}] },
    { key: "pain",           q: "Painful?",         hi_q: "क्या दर्द है?",             opts: YN },
    { key: "blurred-vision", q: "Vision affected?", hi_q: "क्या दिखने पर असर है?",     opts: YN },
    { key: "contact-lens",   q: "Contact lens user?", hi_q: "क्या आप कॉन्टैक्ट लेंस पहनते हैं?", opts: YN },
  ],
  "watering": [
    { key: "duration", q: "For how long?", hi_q: "कब से?",           opts: [{v:"days",label:"Days",hi:"कुछ दिन"},{v:"weeks",label:"Weeks",hi:"हफ्ते"},{v:"months",label:"Months+",hi:"महीनों से"}] },
    { key: "one-eye",  q: "One eye only?", hi_q: "क्या केवल एक आंख में?", opts: YN },
  ],
  "itching": [
    { key: "seasonal", q: "Worse in a particular season?", hi_q: "क्या किसी मौसम में ज़्यादा होता है?", opts: YN },
    { key: "watery",   q: "Watery discharge?",             hi_q: "क्या पानी जैसा स्राव है?",           opts: YN },
  ],
  "flashes": [
    { key: "new",        q: "Are these new (last 4 weeks)?", hi_q: "क्या ये नई हैं (पिछले 4 हफ़्तों में)?", opts: YN },
    { key: "floaters",   q: "Any new floaters too?",          hi_q: "क्या नए तैरते धब्बे भी हैं?",           opts: YN },
    { key: "high-myopia",q: "High minus number (myopia)?",    hi_q: "क्या अधिक माइनस नंबर (myopia) है?",   opts: YN },
  ],
  "floaters": [
    { key: "new",          q: "Are these new floaters?",        hi_q: "क्या ये नए तैरते धब्बे हैं?",       opts: YN },
    { key: "many",         q: "Sudden shower of floaters?",     hi_q: "क्या अचानक बहुत सारे धब्बे दिखे?",  opts: YN },
    { key: "flashes",      q: "Any flashes of light?",           hi_q: "क्या बिजली जैसी चमक भी है?",       opts: YN },
    { key: "black-curtain",q: "Any curtain or shadow in vision?",hi_q: "क्या दृष्टि में परदा या छाया है?",   opts: YN },
  ],
  "black-curtain": [
    { key: "onset",   q: "When did it start?",     hi_q: "यह कब शुरू हुआ?",   opts: [{v:"hours",label:"Hours",hi:"कुछ घंटे पहले"},{v:"day",label:"Today",hi:"आज"},{v:"days",label:"Days ago",hi:"कुछ दिन पहले"}] },
    { key: "flashes", q: "Any flashes of light?", hi_q: "क्या बिजली जैसी चमक है?", opts: YN },
  ],
  "double-vision": [
    { key: "resolves-covered", q: "Goes away if you cover one eye?", hi_q: "क्या एक आंख बंद करने पर ठीक हो जाता है?", opts: YN },
    { key: "headache",         q: "New headache?",                    hi_q: "क्या नया सिरदर्द है?",                    opts: YN },
    { key: "trauma",           q: "Any recent injury?",                hi_q: "क्या हाल में कोई चोट लगी है?",             opts: YN },
  ],
  "swelling": [
    { key: "pain",            q: "Painful?",         hi_q: "क्या दर्द है?",           opts: YN },
    { key: "fever",           q: "Any fever?",       hi_q: "क्या बुखार है?",          opts: YN },
    { key: "vision-affected", q: "Vision affected?", hi_q: "क्या दिखने पर असर है?",   opts: YN },
  ],
  "eye-injury": [
    { key: "sharp-object",  q: "Sharp object involved?",       hi_q: "क्या कोई नुकीली वस्तु लगी है?",     opts: YN },
    { key: "metal",         q: "Any chance of metal fragment?", hi_q: "क्या लोहे का टुकड़ा जाने की संभावना है?", opts: YN },
    { key: "bleeding",      q: "Bleeding from the eye?",         hi_q: "क्या आंख से खून बह रहा है?",         opts: YN },
    { key: "vision-reduced",q: "Vision reduced?",                hi_q: "क्या दृष्टि कम हो गई है?",            opts: YN },
  ],
  "foreign-body": [
    { key: "size",    q: "Size of the object",  hi_q: "वस्तु का आकार",       opts: [{v:"small",label:"Speck / dust",hi:"धूल का कण"},{v:"visible",label:"Small object",hi:"छोटी वस्तु"},{v:"large",label:"Large",hi:"बड़ी वस्तु"}] },
    { key: "removed", q: "Have you removed it?", hi_q: "क्या आपने इसे निकाल दिया है?", opts: YN },
  ],
  "chemical-injury": [
    { key: "chemical-exposure", q: "Confirm chemical splash to the eye?", hi_q: "क्या आंख में केमिकल गिरने की पुष्टि है?", opts: YN, defaultYes: true },
    { key: "irrigated",         q: "Already irrigated with water?",       hi_q: "क्या पानी से धो लिया गया है?",             opts: YN },
  ],
  "light-sensitivity": [
    { key: "pain",           q: "Painful?",         hi_q: "क्या दर्द है?",         opts: YN },
    { key: "blurred-vision", q: "Blurred vision?",   hi_q: "क्या धुंधला दिखता है?", opts: YN },
    { key: "red",            q: "Red eye?",         hi_q: "क्या आंख लाल है?",       opts: YN },
  ],
  "headache-with-eye-pain": [
    { key: "nausea",         q: "Nausea or vomiting?", hi_q: "क्या मतली या उल्टी है?",    opts: YN },
    { key: "blurred-vision", q: "Blurred vision?",     hi_q: "क्या धुंधला दिखता है?",     opts: YN },
    { key: "one-side",       q: "One side of head only?", hi_q: "क्या सिर के केवल एक तरफ है?", opts: YN },
  ],
  "white-reflex-in-child": [
    { key: "photos", q: "Noticed in flash photos?", hi_q: "क्या फ्लैश फोटो में दिखा है?", opts: YN, defaultYes: true },
  ],
  "squint": [
    { key: "duration",     q: "How long?",              hi_q: "कब से?",                opts: [{v:"weeks",label:"Weeks",hi:"हफ़्तों से"},{v:"months",label:"Months",hi:"महीनों से"},{v:"lifelong",label:"Since childhood",hi:"बचपन से"}] },
    { key: "double-vision",q: "Is there double vision?", hi_q: "क्या दो-दो दिखता है?",  opts: YN },
  ],
  "child-not-seeing-properly": [
    { key: "one-eye",        q: "One eye more than the other?",  hi_q: "क्या एक आंख दूसरी से अधिक?",         opts: YN },
    { key: "school-noticed", q: "Noticed at school / by a teacher?", hi_q: "क्या स्कूल में या शिक्षक ने बताया?", opts: YN },
  ],
  "others": [
    { key: "describe-vision", q: "Any vision change?", hi_q: "क्या दृष्टि में कोई बदलाव है?", opts: YN },
    { key: "describe-pain",   q: "Any pain?",           hi_q: "क्या कोई दर्द है?",            opts: YN },
  ],
};

export const MEDICAL_HISTORY = [
  { key: "diabetes",         label: "Diabetes",                       hi: "मधुमेह (डायबिटीज़)" },
  { key: "hypertension",     label: "Hypertension",                   hi: "उच्च रक्तचाप" },
  { key: "glaucoma",         label: "Glaucoma",                       hi: "काला मोतिया (ग्लूकोमा)" },
  { key: "high-myopia",      label: "High myopia",                    hi: "अधिक माइनस नंबर (हाई मायोपिया)" },
  { key: "retina-surgery",   label: "Retinal surgery",                hi: "रेटिना की सर्जरी" },
  { key: "cataract-surgery", label: "Cataract surgery (recent)",      hi: "हाल में मोतियाबिंद का ऑपरेशन" },
  { key: "eye-injections",   label: "Recent eye injections",          hi: "हाल में आंख के इंजेक्शन" },
  { key: "steroid-use",      label: "Steroid use (drops or oral)",    hi: "स्टेरॉयड का उपयोग (ड्रॉप या दवा)" },
  { key: "contact-lens",     label: "Contact lens wearer",            hi: "कॉन्टैक्ट लेंस पहनने वाले" },
  { key: "autoimmune",       label: "Autoimmune disease",             hi: "ऑटो-इम्यून रोग" },
  { key: "none",             label: "None of the above",              hi: "इनमें से कोई नहीं" },
];

/* ─────────────────────────── WIZARD UI STRINGS ─────────────────────────── */
// Static translations for the wizard chrome — step titles, hints, buttons.
export const WIZARD_STRINGS = {
  en: {
    stepOf: (n, total) => `Step ${n} of ${total}`,
    back: "Back",
    continue: "Continue",
    skip: "Skip",
    running: "Running…",
    seeResult: "See my result",
    step1_title: "What is the patient's age?",
    step1_hint:  "This helps us calibrate the urgency — very young and older patients need more caution.",
    step2_title: "Gender (optional)",
    step2_hint:  "Some eye conditions have a gender bias — you can skip this if you prefer.",
    step3_title: "Which eye is affected?",
    step4_title: "Pick the main symptom.",
    step4_hint:  "Choose the one that bothers you most. Red icons flag conditions we treat as red-flag emergencies.",
    step5_title: "A few follow-up questions.",
    step5_hint:  "These help the engine make a safer, more relevant recommendation.",
    step5_noneMsg: "No extra questions for this symptom — click continue.",
    step6_title: "Any relevant medical history?",
    step6_hint:  "Tick every option that applies. This is used only for the assessment — nothing is shared.",
    notes_title: "Anything else you'd like to add? (Optional)",
    notes_placeholder: "Describe your symptom in your own words — or tap the microphone and speak. This helps the assessment be more accurate.",
    notes_footer_supported: "Voice dictation uses your device's speech engine — nothing is sent to third-party servers.",
    notes_footer_unsupported: "Voice dictation is not supported by this browser — please type in the box above.",
    mic_speak: "Speak",
    mic_listening: "Listening…",
    photo_title: "Optional — attach a photo of the eye",
    photo_desc: "A daylight photo of the red or swollen eye helps Dr. Shantanu or Dr. Manisha review your case before you arrive. This is completely optional.",
    photo_attach: "Attach a photo",
    photo_uploading: "Uploading…",
    loading_kicker: "Analysing your answers",
    loading_title: "Reviewing your symptoms…",
    loading_title_overtime: "Almost done — just a bit longer…",
    loading_hint: "The AI engine is comparing your answers against the red-flag rules Dr. Shantanu and Dr. Manisha reviewed. This usually takes about 30 seconds.",
    loading_seconds: "seconds",
    loading_wrapping: "Wrapping up",
    loading_bullets: [
      "Applying deterministic red-flag rules first",
      "Cross-referencing age band and medical history",
      "Drafting a plain-language explanation for you",
    ],
    result_redflag: "Red-flag rule",
    result_causes: "Possible causes (not diagnosis)",
    result_matters: "Why this matters",
    result_watch: "Watch for these — call the clinic if any appear",
    result_next: "Next step",
    cta_wa_title: "WhatsApp our team",
    cta_wa_sub: "Dr. Shantanu & Dr. Manisha personally review incoming messages — we reply within 24 hours.",
    cta_book: "Book an appointment",
    cta_call: "Call the hospital",
    cta_map: "Directions",
    restart: "Start a new assessment",
    important_label: "Important:",
    fallback_summary: "We could not run the AI assessment right now.",
    fallback_label: "Please book an ophthalmology examination this week.",
    fallback_why: "The engine is temporarily unavailable — a face-to-face examination is the reliable next step.",
    fallback_action: "Book an eye examination within the next week.",
    fallback_warnings: [
      "Any sudden worsening of vision",
      "New pain in or around the eye",
      "New flashes, floaters or shadows",
    ],
  },
  hi: {
    stepOf: (n, total) => `चरण ${n} / ${total}`,
    back: "पीछे",
    continue: "आगे बढ़ें",
    skip: "छोड़ें",
    running: "प्रक्रिया चल रही है…",
    seeResult: "परिणाम देखें",
    step1_title: "रोगी की उम्र क्या है?",
    step1_hint:  "उम्र से हम आपातकाल का सही आकलन कर पाते हैं — बहुत छोटे बच्चे और बुजुर्गों में सावधानी अधिक ज़रूरी है।",
    step2_title: "लिंग (वैकल्पिक)",
    step2_hint:  "कुछ आंखों की बीमारियों में लिंग का प्रभाव होता है — चाहें तो इसे छोड़ सकते हैं।",
    step3_title: "कौन-सी आंख प्रभावित है?",
    step4_title: "मुख्य लक्षण चुनें।",
    step4_hint:  "जो सबसे अधिक परेशान कर रहा है, वह चुनें। लाल आइकन वाले लक्षण आपातकालीन मानें जाते हैं।",
    step5_title: "कुछ आगे के प्रश्न।",
    step5_hint:  "ये उत्तर सुरक्षित और सटीक सलाह देने में मदद करते हैं।",
    step5_noneMsg: "इस लक्षण के लिए कोई अतिरिक्त प्रश्न नहीं — आगे बढ़ें।",
    step6_title: "क्या कोई पुरानी बीमारी या इतिहास है?",
    step6_hint:  "जो लागू हों उन्हें चुनें। यह जानकारी केवल इस आकलन के लिए है — कहीं साझा नहीं की जाती।",
    notes_title: "कुछ और जोड़ना चाहेंगे? (वैकल्पिक)",
    notes_placeholder: "अपने शब्दों में लक्षण बताइए — या माइक पर टैप कर के बोलिए। इससे आकलन अधिक सटीक होगा।",
    notes_footer_supported: "वॉइस डिक्टेशन आपके डिवाइस के स्पीच इंजन से होता है — कोई डेटा तीसरे सर्वर पर नहीं जाता।",
    notes_footer_unsupported: "इस ब्राउज़र में वॉइस डिक्टेशन उपलब्ध नहीं है — कृपया ऊपर लिखें।",
    mic_speak: "बोलें",
    mic_listening: "सुन रहे हैं…",
    photo_title: "वैकल्पिक — आंख की फोटो जोड़ें",
    photo_desc: "लाल या सूजी आंख की दिन में ली गई फोटो से डॉ. शांतनु या डॉ. मनीषा को आपकी स्थिति पहले से देखने में मदद मिलती है। यह पूरी तरह वैकल्पिक है।",
    photo_attach: "फोटो जोड़ें",
    photo_uploading: "अपलोड हो रहा है…",
    loading_kicker: "आपके उत्तरों का विश्लेषण",
    loading_title: "आपके लक्षणों की समीक्षा हो रही है…",
    loading_title_overtime: "लगभग तैयार — बस थोड़ा और…",
    loading_hint: "AI इंजन आपके उत्तरों को डॉ. शांतनु और डॉ. मनीषा के तय किए red-flag नियमों से मिला रहा है। इसमें आमतौर पर 30 सेकंड लगते हैं।",
    loading_seconds: "सेकंड",
    loading_wrapping: "समाप्ति की ओर",
    loading_bullets: [
      "पहले red-flag नियम लागू कर रहे हैं",
      "उम्र और चिकित्सा इतिहास से मिलान कर रहे हैं",
      "आपके लिए सरल भाषा में समझ तैयार कर रहे हैं",
    ],
    result_redflag: "Red-flag नियम",
    result_causes: "संभावित कारण (निदान नहीं)",
    result_matters: "यह क्यों महत्वपूर्ण है",
    result_watch: "इन पर ध्यान दें — यदि दिखें तो क्लिनिक को कॉल करें",
    result_next: "अगला कदम",
    cta_wa_title: "हमारी टीम को WhatsApp करें",
    cta_wa_sub: "डॉ. शांतनु और डॉ. मनीषा स्वयं संदेश देखते हैं — 24 घंटे में उत्तर।",
    cta_book: "अपॉइंटमेंट बुक करें",
    cta_call: "अस्पताल को कॉल करें",
    cta_map: "दिशा-निर्देश",
    restart: "नया आकलन शुरू करें",
    important_label: "महत्वपूर्ण:",
    fallback_summary: "अभी AI आकलन नहीं चल पा रहा है।",
    fallback_label: "कृपया इस हफ़्ते नेत्र-परीक्षण के लिए अपॉइंटमेंट लें।",
    fallback_why: "इंजन अस्थायी रूप से उपलब्ध नहीं है — आमने-सामने परीक्षण ही सुरक्षित अगला कदम है।",
    fallback_action: "अगले हफ़्ते के अंदर आंखों की जाँच कराएँ।",
    fallback_warnings: [
      "दृष्टि में अचानक कोई भी गिरावट",
      "आंख में या आसपास नया दर्द",
      "नई बिजली जैसी चमक, तैरते धब्बे या परदा",
    ],
  },
};
